/**
 * Event Log Tab - Real-time event stream
 */

import { useState, useEffect, useRef } from 'react';
// import { Stack } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import { Text, Button, Switch, Badge, Paper, Group, Stack } from '../../../../components/ui';
import { DataTable, type DataTableColumn } from '../../../../components/advanced';
import { getStatusColor } from '../../../../utils/presenceHelpers';
import { usePresenceManager } from '../../../../hooks/usePresenceManager';
import type { EventLogEntry } from '../types';
import type { UserPresenceUpdate, PresenceStatus } from '@actschurch/ccms-sdk';

export function EventLogTab() {
  const { manager } = usePresenceManager();
  const [eventLog, setEventLog] = useState<EventLogEntry[]>([]);
  const [eventLogPaused, setEventLogPaused] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const eventLogScrollRef = useRef<HTMLDivElement>(null);

  // Subscribe to real-time events
  useEffect(() => {
    if (!manager || eventLogPaused) return;

    const unsubscribers = [
      manager.on('userOnline', (user: UserPresenceUpdate) => {
        setEventLog((prev) =>
          [
            {
              id: `${Date.now()}-${Math.random()}`,
              timestamp: new Date(),
              type: 'userOnline' as const,
              userId: user.userId,
              userName: user.userName,
              status: user.status as PresenceStatus,
              platform: user.platform,
              data: user,
            },
            ...prev,
          ].slice(0, 100)
        );
      }),

      manager.on('userOffline', (user: UserPresenceUpdate) => {
        setEventLog((prev) =>
          [
            {
              id: `${Date.now()}-${Math.random()}`,
              timestamp: new Date(),
              type: 'userOffline' as const,
              userId: user.userId,
              userName: user.userName,
              data: user,
            },
            ...prev,
          ].slice(0, 100)
        );
      }),

      manager.on('statusChanged', (user: UserPresenceUpdate) => {
        setEventLog((prev) =>
          [
            {
              id: `${Date.now()}-${Math.random()}`,
              timestamp: new Date(),
              type: 'statusChanged' as const,
              userId: user.userId,
              userName: user.userName,
              status: user.status as PresenceStatus,
              platform: user.platform,
              data: user,
            },
            ...prev,
          ].slice(0, 100)
        );
      }),
    ];

    return () => {
      unsubscribers.forEach((unsub) => unsub());
    };
  }, [manager, eventLogPaused]);

  // Auto-scroll to top when new events arrive
  useEffect(() => {
    if (autoScroll && eventLogScrollRef.current && eventLog.length > 0) {
      eventLogScrollRef.current.scrollTop = 0;
    }
  }, [eventLog, autoScroll]);

  const columns: DataTableColumn<EventLogEntry>[] = [
    {
      key: 'timestamp',
      label: 'Time',
      width: 100,
      render: (_value, row) => new Date(row.timestamp).toLocaleTimeString(),
    },
    {
      key: 'type',
      label: 'Event',
      width: 150,
      render: (_value, row) => (
        <Badge
          color={
            row.type === 'userOnline' ? 'green' : row.type === 'userOffline' ? 'gray' : 'blue'
          }
        >
          {row.type}
        </Badge>
      ),
    },
    {
      key: 'userId',
      label: 'User ID',
      width: 200,
      render: (_value, row) => row.userName || row.userId || 'System',
    },
    {
      key: 'details',
      label: 'Details',
      render: (_value, row) => (
        <Group gap="xs">
          {row.status && (
            <Badge color={getStatusColor(row.status)} size="xs">
              {row.status}
            </Badge>
          )}
          {row.platform && (
            <Badge size="xs" variant="light">
              {row.platform}
            </Badge>
          )}
        </Group>
      ),
    },
  ];

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          Real-time event stream ({eventLog.length} events)
        </Text>
        <Group>
          <Button
            size="xs"
            variant="light"
            leftSection={<IconTrash size={14} />}
            onClick={() => setEventLog([])}
          >
            Clear Log
          </Button>
          <Switch
            label="Pause"
            checked={eventLogPaused}
            onChange={(e) => setEventLogPaused(e.currentTarget.checked)}
            size="sm"
          />
          <Switch
            label="Auto-scroll"
            checked={autoScroll}
            onChange={(e) => setAutoScroll(e.currentTarget.checked)}
            size="sm"
          />
        </Group>
      </Group>

      {/* Event Log */}
      <Paper p="md" withBorder style={{ maxHeight: '500px', overflow: 'auto' }} ref={eventLogScrollRef}>
        <DataTable
          columns={columns}
          data={eventLog}
          enableInternalSearch={false}
          emptyMessage="No events yet. Waiting for real-time updates..."
        />
      </Paper>
    </Stack>
  );
}
